import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { TaskFilter } from "../../types";

export function useDeleteFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("task_filters")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },

    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.filters });

      const prevFilters = queryClient.getQueryData<TaskFilter[]>(queryKeys.filters);

      // Left alone when the cache has not loaded: writing `[]` into it would
      // flip useFilters out of its pending state and drop the app's loading
      // gate, and onError's snapshot guard could not put it back.
      queryClient.setQueryData<TaskFilter[]>(queryKeys.filters, (old) =>
        old ? old.filter((f) => f.id !== id) : old
      );

      return { prevFilters };
    },

    onError: (err, _, context) => {
      if (context?.prevFilters)
        queryClient.setQueryData<TaskFilter[]>(queryKeys.filters, context.prevFilters);
      Sentry.captureException(err, { extra: { mutation: "useDeleteFilter" } });
      toast.error("Failed to delete filter. Please try again.");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.filters });
    },
  });
}
